/******************************************************************************************
 *	Customers Controller
 ******************************************************************************************/
(function() {
  angular
    .module('rcControllers.customers', [])
    .controller('CustomersController', customersController);
  /* @ngInject */
  function customersController(
    $log,
    $scope,
    $state,
    _,
    AddCustomerModal,
    ResellerChangeModal,
    AlertModal,
    CancelCustomerInvitationModal,
    CustomerReminderModal,
    Customers,
    CustomerSegments,
    CustomersListSnapshot,
    CustomersNarrowDownOptions,
    feature,
    LicenseSummaryUtility,
    Payload,
    PLAN_TYPE,
    SelectedCustomer,
    Settings,
    User
  ) {
    /*=== Customers List ===*/
    $scope.systemStatusLink = function() {
      window.open('https://cbconnection.adobe.com/en/system-status.html', '_blank');
    };

    //Make sure the view stats with a clean Payload.
    //Otherwise it will get persisted between navigating to various other pages (because it's a Service).
    Payload.reset();

    //List of states which, if are the fromState name, will trigger restoring CustomersListSnapshot if a snapshot exists
    //This is needed to avoid restoring an old snapshot when coming from an unrelated view.
    //For example, Customer Details should restore the snapshot, however Dashboard should not.
    //So going from Customer Details, to Dashboard, to Customer List should not restore any snapshots
    var persistanceStates = [
      'customerDetails',
      'licenseAdmin.impersonatedReseller.customerDetails'
    ];

    // States that use this controller, and should have data loaded when switched to
    const customerSegmentStates = [
      'customers',
      'customers.defaultSegment',
      'customers.segments',
      'licenseAdmin.impersonatedReseller.customers',
      'licenseAdmin.impersonatedReseller.customers.defaultSegment',
      'licenseAdmin.impersonatedReseller.customers.segments'
    ];

    $scope.$on('$stateChangeStart', function() {
      //disable delete mode
      $scope.deleteModeAllowed = false;
    });

    $scope.$on('$stateChangeSuccess', function(event, toState, toParams, fromState) {
      // if we're switching TO the customer segment state, perform these reset/snapshot actions
      if (_.includes(customerSegmentStates, _.get(toState, 'name'))) {
        //Get current segment and make available to view
        $scope.currentSegment = CustomerSegments.getCurrentSegment(toParams);

        //Clear list so that old list doesn't get new headers in UI
        $scope.customers = null;

        $scope.isNoProductsSegment =
          $scope.currentSegment === CustomerSegments.getNoProductsSegment();

        // Reset to default (not delete) mode when switching sections.
        $scope.deleteModeEnabled = false;
        // Only allow deletion from the No Products section (or Pending Invitations if it's still around)
        $scope.deleteModeAllowed = $scope.isNoProductsSegment;

        //If the fromState.name is in the list of states that trigger restoring the CustomersListSnapshot and if the snapshot exists, restore it
        //The snapshot will be cleared after it's restored, however it will not be cleared if the fromState doesn't trigger a restore.
        //To understand why, consider this scenario: Customer List -[snapshot]-> Customer Details -> Dashboard -> Customer List.
        //In this situation, the snapshot is not restored, because the Dashboard is not in the list of trigger states.
        //However, the snapshot is not disposed of. This is to allow the user to use the browser back-button to navigate back to the initial Customer List and have it in the same state.
        if (
          fromState &&
          fromState.name &&
          _.includes(persistanceStates, fromState.name) &&
          CustomersListSnapshot.getPayload()
        ) {
          //Payload for server call
          Payload.set(CustomersListSnapshot.getPayload());

          //Restore UI state
          //Note: Pagination is restored from the Payload after getCustomers() call returns data.
          $scope.sidebarExpanded = CustomersListSnapshot.isSidebarExpanded(); //Restore sidebar state
          $scope.currentSort = CustomersListSnapshot.getCurrentSort(); //$scope.currentSort for sort arrows on sortableTableColumn directive instances
          $scope.managedByMeCheckbox = Payload.getOwnerFilter() === User.getID(); //Managed by me checkbox state
          $scope.initialSearchQuery = Payload.getSearchAsString(); //Expand search and display query. If getSearchAsString() returns null, it will be ignored.
          $scope.planTypeTeamCheckbox = Payload.hasPlanTypeFilterForTeam();
          $scope.planTypeEnterpriseCheckbox = Payload.hasPlanTypeFilterForEnterprise();

          //Clear so that it doesn't persist between segments
          CustomersListSnapshot.clear();
        } else {
          //Otherwise, prevent payload persisting between segments
          Payload.clear();
        }

        //Get customers for current segment
        getCustomers();

        //Remove Add Customer modal if visible
        AddCustomerModal.remove();
      }
    });

    //Clean up for UI and payload when get customer calls finish successfully or not
    //This would normally go into a .finally() method, however, when calls are aborted, the finally() gets called.
    //To prevent this, we call newCustomerDataCleanUp() inside the success handler and in the error handler when status is not 0 (aka aborted)
    var newCustomerDataCleanUp = function() {
      //Reflect payload changes (if any) to $scope
      //This is done here instead of when the call is made so that the UI doesn't change before the new data arrives
      $scope.currentPage = Payload.getPage();

      //Sorting - if payload doesn't have any sort criteria and there is a default sort, set sort
      //to the first default sort column, otherwise reset.
      if (!Payload.getSort()) {
        // The server returns the default sort field(s) delimited by commas.
        // Since the UI only has the able to show a single sort column indicator, parse out the
        // first server sort field.
        // For each column there is a rc-sortable-column and a server-columns directive.
        // These are often the same but no always so we have to do a lookup.
        //
        // The default server sort fields for each segment are:
        //
        // no products: "name,anniversaryDate"
        // pendingInvitations: "lastInviteDate,name" (Last Invitation)
        // pendingRequests: "-pendingRequest.seatCount,pendingRequest.daysCount"
        // pastDueRequest: "-pastDueRequest.seatCount,-pastDueRequest.daysCount". (Licenses)
        // upcomingRenewals: "timeToUpcomingRenewal,name"
        // readyForRenewal: "renewalWindowInfo.daysLeftInWindow,renewalWindowInfo.paidSeats"
        // all customers: "name,anniversaryDate"

        var defaultSort = $scope.defaultSort ? _.split($scope.defaultSort, ',') : undefined;

        if (defaultSort && defaultSort[0]) {
          var serverColumn = defaultSort[0];
          var direction;

          var getSortableColumnFromServerColumn = function(serverColumn) {
            var columnLookup = {
              'pendingRequest.daysCount': 'daysCount',
              'pendingRequest.seatCount': 'seatCount',
              'pastDueRequest.daysCount': 'daysCount',
              'pastDueRequest.seatCount': 'seatCount',
              'renewalWindowInfo.daysLeftInWindow': 'timeLeft',
              'renewalWindowInfo.paidSeats': 'paidSeats',
              'renewalWindowInfo.totalSeats': 'totalSeats',
              'renewalWindowInfo.unpaidSeats': 'seatsNotRenewed',
              'delegationSummary.totalSummary.totalSeatCount': 'totalSeatCount',
              'delegationSummary.totalSummary.paidSeatCount': 'paidSeatCount',
              'delegationSummary.totalSummary.unpaidSeatCount': 'unpaidSeatCount',
              'delegationSummary.totalSummary.activeSeatCount': 'activeSeatCount',
              'delegationSummary.totalSummary.availableSeatCount': 'availableSeatCount'
            };

            return columnLookup[serverColumn] ? columnLookup[serverColumn] : serverColumn;
          };

          // Sort fields which are descending are prefixed with '-'.
          if (serverColumn.charAt(0) === '-') {
            direction = 'desc';
            serverColumn = serverColumn.slice(1);
          } else {
            direction = 'asc';
          }

          // Set $scope.currentSort for sort arrows on rc-sortable-columns.
          $scope.currentSort = {
            column: getSortableColumnFromServerColumn(serverColumn),
            direction: direction
          };
        } else {
          $scope.currentSort = {};
        }
      }

      //Controls "no data" view
      $scope.noCustomers = !$scope.customers;

      //Hides loading indicator
      $scope.loadingCustomers = false;

      //Highlights counters if filter is applied or active search.
      $scope.highlightCounters =
        Payload.hasOwnerFilter() || Payload.hasPlanTypeFilter() || Payload.hasSearch();
    };

    //Get customers method that deals with success, error and loading indicator.
    var getCustomers = function() {
      $log.info('%cgetCustomers() payload ::', 'font-weight:bold;color:#009966;', Payload.get());

      $scope.loadingCustomers = true; //Shows loading indicator

      Customers.get($scope.currentSegment.path, Payload.get())
        .then(function(responseData) {
          //Update customers list
          $scope.customers = _.get(responseData, 'customers')
            ? _.filter(responseData.customers, customer => !_.isNull(customer))
            : null;

          //Update customer segments
          CustomerSegments.updateCounters(responseData.meta);

          // Look at license delegation data, and create some metadata to help us display appropriately.
          // metadata includes whether there's an error fetching the data, over-activated licenses, etc
          _.forEach($scope.customers, LicenseSummaryUtility.createLicenseSummaryMetadata);

          //Update pagination data
          $scope.totalPages = responseData.pagination.total || 1;
          // Reset the current page in case the customer list was reduced by this request and the requested currentPage no longer exists.
          setCurrentPage(responseData.pagination.current);

          $scope.defaultSort = responseData.defaultSort;

          newCustomerDataCleanUp();
        })
        .catch(function(response) {
          if (response.status !== 0) {
            //status is 0 when call is aborted
            //Get first character from HTTP status as number
            var statusType = +('' + response.status).charAt(0);

            if (response.status === 404) {
              $scope.errorMessage = 'global.dataList.emptyMessages.noResultsFound';

              //Update customer segments
              CustomerSegments.updateCounters(response.data.meta);
            } else if (statusType === 5) {
              $scope.errorMessage = 'global.dataList.emptyMessages.dataError';
            }

            //Remove customers and totalPages
            $scope.customers = null;
            $scope.totalPages = 0;

            $scope.defaultSort = null;

            newCustomerDataCleanUp();
          }
        });
    };

    var getCustomerIndexInScope = function(customerId) {
      return _.findIndex($scope.customers, ['id', customerId]);
    };

    /*=== Pagination ===*/

    var setCurrentPage = function(page) {
      $scope.currentPage = page;
      Payload.setPage(page);
    };

    //Request data for specified page
    $scope.goToPage = function(page) {
      Payload.setPage(page);
      getCustomers();
    };

    /*=== Sorting ===*/

    var clearSort = function() {
      Payload.clearSort();
      $scope.currentSort = {};
    };

    //Sends a request to the server with sorting information
    //column argument is the name of the column being sorted
    //serverColumns argument is the columns the server needs to sort by. Used for composite columns that aggregate multiple fields in one column.
    //serverColumns can specify multiple columns separated by comma.
    //In most cases, a column will only hold one piece of data so column and serverColumns will be the same: e.g. anniversaryDate column will be sorted by anniversaryDate on the server
    //However, in cases like the Managed By column, the serverColumns will actually be managedBy.firstName,managedBy.lastName because the server needs to sort by both
    //For this reason, if serverColumns is not specified, it will be inferred from column.
    $scope.sort = function(column, serverColumns) {
      //If data is not set, it means that the column being sorted has the same name as the column on the server
      if (!serverColumns) {
        serverColumns = column;
      }

      if ($scope.currentSort.column === column) {
        //Same sort column clicked
        if ($scope.currentSort.direction === 'asc') {
          //Was `asc`, means user wants `desc`
          $scope.currentSort.direction = 'desc';
        } else {
          //Was `desc`, means user wants `asc`
          $scope.currentSort.direction = 'asc';
        }
      } else {
        //New sort column clicked
        $scope.currentSort.column = column;
        $scope.currentSort.direction = 'asc';
      }

      //Add sort data to payload
      //columnName means asc, -columnName means desc
      if ($scope.currentSort.column) {
        Payload.setSort(serverColumns, $scope.currentSort.direction);
      } else {
        Payload.clearSort();
      }

      //When user sorts a column, reset paging
      setCurrentPage(1);
      getCustomers();
    };

    /*=== Search ===*/
    //Provide options for narrow down dropdown
    $scope.narrowDownOptions = CustomersNarrowDownOptions;

    $scope.search = function(query, field) {
      Payload.setSearch(query, field);
      setCurrentPage(1);
      getCustomers();
    };

    $scope.clearSearch = function() {
      Payload.clearSearch();
      setCurrentPage(1);
      getCustomers();
    };

    /*=== Filter: "Managed by me" ===*/
    $scope.filterManagedByMe = function(managedByMeChecked) {
      if (!User.isLicenseAdmin()) {
        $scope.managedByMeCheckbox = managedByMeChecked;
        if (managedByMeChecked) {
          //"Managed by me" checkbox checked
          Payload.setOwnerFilter(User.getID());
        } else {
          //"Managed by me" checkbox unchecked
          Payload.clearOwnerFilter();
        }
        setCurrentPage(1);
        getCustomers();
      }
    };

    /*=== Filter: "Plan Type" ===*/
    $scope.filterPlanType = function() {
      Payload.clearPlanTypeFilter();

      if ($scope.planTypeTeamCheckbox) {
        Payload.setPlanTypeFilter(PLAN_TYPE.TEAM);
      }

      if ($scope.planTypeEnterpriseCheckbox) {
        Payload.setPlanTypeFilter(PLAN_TYPE.ENTERPRISE);
      }
      setCurrentPage(1);
      getCustomers();
    };

    var isNewCustomerVisisble = function(customer) {
      if (Payload.hasPlanTypeFilter()) {
        var planType = customer.requestedPlanType;

        return (
          (Payload.hasPlanTypeFilterForTeam() && planType === PLAN_TYPE.TEAM) ||
          (Payload.hasPlanTypeFilterForEnterprise() && planType === PLAN_TYPE.ENTERPRISE)
        );
      }

      return true;
    };

    /*=== Table Action Buttons ===*/
    //View Details
    $scope.viewCustomerDetails = function(event, customer) {
      //If Ctrl+Click, or third mouse button click let the browser handle the click event by opening a new tab
      //Otherwise, preventDefault() and handle the click to save state.
      if (!event.ctrlKey && event.button !== 1) {
        event.preventDefault();
        SelectedCustomer.set(customer);
        CustomersListSnapshot.set(
          $scope.currentSegment.path,
          Payload.get(),
          $scope.sidebarExpanded,
          $scope.currentSort
        );

        //Depending on user role, customer details state can have different state names
        var customerDetailsStateName = User.isLicenseAdmin()
          ? 'licenseAdmin.impersonatedReseller.customerDetails'
          : 'customerDetails';
        $state.go(customerDetailsStateName, {customerID: customer.id});
      }
    };

    /*=== Add Customer Modal ===*/
    $scope.showAddCustomerModal = function() {
      if (User.isResellerMember() || User.isResellerAdmin() || User.isLicenseAdminReadWrite()) {
        AddCustomerModal.create().then(function(data) {
          //If modal wasn't cancelled (closed without submit)
          if (data && !data.cancelled) {
            if (_.get(data, 'response.userHasDifferentAdobeAccount')) {
              AlertModal.create({
                additionalStyling: 'customer-has-different-adobe-account-modal',
                message: 'customerHasDifferentAdobeAccountModal.message',
                title: 'customerHasDifferentAdobeAccountModal.title',
                translateValues: {email: _.get(data, 'response.email')},
                type: 'notice'
              });
            }
            // New customer may not be visible, or reflected in count, if plan type filter enabled.
            var showCustomer = isNewCustomerVisisble(data.response);

            if (showCustomer) {
              //Add new item to the segment if the current segment is the No Products or All Customers segments.
              if (
                $scope.currentSegment === CustomerSegments.getNoProductsSegment() ||
                $scope.currentSegment === CustomerSegments.getAllCustomersSegment()
              ) {
                // Clear any sort. This does not re-fetch the customers so the next page request
                // will be unsorted and may contain some of the same customers as on this page.
                clearSort();

                //If customers was null, instantiate it to a new array in which to unshift the new data.
                if (!$scope.customers) {
                  $scope.customers = [];
                }

                //Add new customer first in the array
                $scope.customers.unshift(data.response);

                //If the list was empty before adding a new customer, invalidate the noCustomers var
                $scope.noCustomers = false;
              }

              //Increment the No Products and All counters by 1.
              CustomerSegments.incrementSegmentCounter(CustomerSegments.getNoProductsSegment());
              CustomerSegments.incrementSegmentCounter(CustomerSegments.getAllCustomersSegment());
            }
          }
        });
      }
    };

    $scope.displayResellerChangeModal = true;
    $scope.resellerChangeModal = false;

    /*=== Add Reseller Change Modal ===*/
    $scope.showResellerChangeModal = function() {
      ResellerChangeModal.create().then(function() {
        $scope.resellerChangeModal = true;
      });
    };

    $scope.toggleDeleteMode = function() {
      $scope.deleteModeEnabled = !$scope.deleteModeEnabled;
    };

    /*=== Edit Customer Modal ===*/
    $scope.showEditCustomerModal = function(customer) {
      if (User.isResellerMember() || User.isResellerAdmin() || User.isLicenseAdminReadWrite()) {
        var locals = {
          customer: customer
        };

        var getCustomerIndex = function() {
          var count = $scope.customers.length;
          for (var i = 0; i < count; i++) {
            if ($scope.customers[i] === customer) {
              return i;
            }
          }
          return -1;
        };

        AddCustomerModal.create(locals).then(function(data) {
          // If the edited invite was already accepted, refresh the customer data.
          // Otherwise, if modal wasn't cancelled (closed without submit) update the existing customer
          if (data && !data.cancelled) {
            if (data.refreshCustomers) {
              getCustomers();
            } else {
              // Clear any sort. This does not re-fetch the customers so the next page request
              // will be unsorted and may contain some of the same customers as on this page.
              clearSort();

              // If the edited customer is already in the first row, then just update it.
              // Otherwise, remove the old version of the customer and put the new version in the
              // first row.
              var index = getCustomerIndex();
              const editedCustomer = _.has(data, 'response.details')
                ? _.get(data, 'response.details')
                : _.get(data, 'response');
              if (index === 0) {
                $scope.customers[0] = editedCustomer;
              } else if (index > 0) {
                $scope.customers.splice(index, 1);
                $scope.customers.unshift(editedCustomer);
              } else {
                $log.warn('edited customer not found');
              }
            }
          }
        });
      }
    };

    /*=== Send Reminder Modal ===*/
    $scope.sendReminder = function(event, customer) {
      event.preventDefault();

      var locals = {
        customer: {
          id: customer.id,
          email: _.get(customer, 'admin.email'),
          name: customer.name,
          lastInviteDate: customer.lastInviteDate,
          usadEnabled: !!customer.usadEnabled
        }
      };

      CustomerReminderModal.create(locals).then(function(data) {
        if (data && !data.cancelled) {
          //Request customers for current segment
          //data.refreshCustomers is true in the rare case when a send reminder is attempted after a customer already accepted an invitation.
          //It's a rare case because it would mean the customer accepted the invitation after the reseller representative loaded the pending invitations segment, but before the reminder was sent.
          if (data.refreshCustomers) {
            getCustomers();
          } else {
            if (!customer.admin) {
              customer.admin = {};
            }
            customer.admin.email = _.get(data, 'response.email');
            customer.lastInviteDate = _.get(data, 'response.lastInviteDate');
            customer.usadEnabled = _.get(data, 'response.usadEnabled');
          }
        }
      });
    };

    /*=== Cancel Invitation Modal ===*/
    $scope.cancelInvitation = function(customer) {
      var locals = {
        customerID: customer.id,
        email: customer.admin && customer.admin.email ? customer.admin.email : null,
        name: customer.name
      };

      CancelCustomerInvitationModal.create(locals).then(function(data) {
        if (data) {
          if (data.needsRefresh) {
            getCustomers();
          } else if (!data.cancelled) {
            // Remove the customer from the list
            var index = getCustomerIndexInScope(customer.id);
            if (index) {
              $scope.customers.splice(index, 1);
              //Decrement the No Products counter by 1
              CustomerSegments.decrementSegmentCounter(CustomerSegments.getNoProductsSegment());
              // If we have more pending invitations than are shown on the current page (pagination has occurred),
              // check to see if we should reload to display more invitations.
              // We should reload customers if:
              // * We've already removed half the invitations originally fetched for this page, OR
              // * There are few enough total invitations left that we can now show them on a single page.
              var itemsPerPage = Settings.itemsPerPage.default;
              if (
                $scope.currentSegment.counter &&
                $scope.currentSegment.counter > $scope.customers.length &&
                ($scope.customers.length <= itemsPerPage / 2 ||
                  $scope.currentSegment.counter <= itemsPerPage)
              ) {
                getCustomers();
              }
            } else {
              // We couldn't find the customer in the list to remove, so reload the customer data
              getCustomers();
            }
          }
        }
      });
    };

    /*=== Sidebar Segments ===*/
    $scope.segments = CustomerSegments.getSegments();

    /*=== Toggle Sidebar ===*/
    $scope.sidebarExpanded = true; //Default state: expanded
    $scope.toggleSidebar = function() {
      $scope.sidebarExpanded = !$scope.sidebarExpanded;
    };

    /*=== Clean-up ===*/
    $scope.$on('$destroy', function() {
      //Remove any modals if visible, otherwise navigation (via back / forward arrows) would preserve the modal over other views
      AddCustomerModal.remove();
      CustomerReminderModal.remove();
      CancelCustomerInvitationModal.remove();
    });
  }
})();
